/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';
import truncate from 'lodash/truncate';
import upperFirst from 'lodash/upperFirst';
import { formatDistance } from 'date-fns';

// UI lib components
import {
  Stack,
  Container,
  Accordion,
  Group,
  Text,
  Spoiler,
  Card,
  ActionIcon,
  Box,
} from '@mantine/core';

// UI local components
import ConfirmBar from '@ui-kit/molecules/ConfirmBar';
import User from '@app/shared/ui-kit/atoms/User';
import QuestionPriorityBadge from '@app/shared/ui-kit/molecules/QuestionPriorityBadge';
import ContentLoader from '@app/shared/ui-kit/molecules/ContentLoader';
import ResourceBadgeSimple from '@app/features/Resource/ResourceBadgeSimple';

// Store
import dialogStoreVar from '@features/Dialog/store/dialogStore';

// Local API & Types
import {
  GetThreadQuery,
  useGetThreadQuery,
  useConfirmAnswerMutation,
  SortDirection,
} from '@app-types/blackroom-api';
import { DialogBulkConfirmAnswerProps } from './type';
import { QueriedQuestion } from '@app/features/Support/types';

// Hooks
import useResourceClick from '@features/Support/hooks/useResourceClick';
import useRefetchQuestions from '@features/Support/hooks/useRefetchQuestions';

// Assets
import {
  IconCircleDashedCheck as IconNotConfirmed,
  IconCircleCheck as IconConfirmed,
  IconCircleCheckFilled as IconAnswered,
  IconPaperclip as IconAttachedFile,
} from '@tabler/icons-react';

// Style
import * as classes from './styles/index.css';
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';
import { palette } from '@app/shared/ui-kit/colors';
import { ICON_SIZE_MAP } from '@app/shared/ui-kit/icons-sizes';
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function DialogBulkConfirmAnswer({
  questions,
  withIndicator = false,
  ...props
}: DialogBulkConfirmAnswerProps) {
  const workspace = useWorkspace();

  /* ********************************** HOOKS ********************************* */

  // Ref
  const questionsRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // State
  const [expandedQuestionId, setExpandedQuestionId] = useState<string | null>(
    () => {
      // Expand the first unanswered question
      const unansweredQuestion = questions.find(
        (question) => !question.isAnswered && question.answersCount > 0,
      );

      if (unansweredQuestion) return unansweredQuestion.id;

      return null;
    },
  );

  const [confirmedAnswers, setConfirmedAnswers] = useState<
    NonNullable<GetThreadQuery['thread']['nodes']>
  >([]);

  // Scroll to the expanded question
  useEffect(() => {
    if (expandedQuestionId && questionsRefs.current[expandedQuestionId]) {
      questionsRefs.current[expandedQuestionId].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [expandedQuestionId]);

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);
  const { closeDialog } = dialogStore ?? {};

  const refetchQuestions = useRefetchQuestions();

  // Localization
  const { t } = useTranslation(['Common', 'Support']);

  // API
  const [confirmAnswer] = useConfirmAnswerMutation();

  const openResource = useResourceClick();

  /* ******************************** CALLBACKS ******************************* */

  const isAnsweredQuestion = useCallback(
    (question: QueriedQuestion) => {
      const hasTempConfirmedAnswer = confirmedAnswers.some(
        (confirmedAnswer) => confirmedAnswer.parentQuestionId === question.id,
      );

      if (
        !question.isAnswered &&
        question.answersCount > 0 &&
        !hasTempConfirmedAnswer
      ) {
        return false;
      }

      return true;
    },
    [confirmedAnswers],
  );

  const unansweredQuestionCount = useMemo(() => {
    return questions.filter((question) => !isAnsweredQuestion(question)).length;
  }, [questions, confirmedAnswers]);

  const isConfirmedAnswer = useCallback(
    (
      answer: NonNullable<GetThreadQuery['thread']['nodes']>[number],
      parentQuestion: QueriedQuestion,
    ) => {
      // Case: Answer is temporarily confirmed (client-side only)
      if (
        confirmedAnswers.some(
          (confirmedAnswer) => confirmedAnswer.id === answer.id,
        )
      ) {
        return true;
      }

      const parentQuestionHasTempConfirmedAnswers = confirmedAnswers.some(
        (confirmedAnswer) =>
          confirmedAnswer.parentQuestionId === parentQuestion.id,
      );
      // Case: The answer is already confirmed, and the parent question has no other temporarily confirmed answers
      if (answer.isConfirmed && !parentQuestionHasTempConfirmedAnswers) {
        return true;
      }

      return false;
    },
    [confirmedAnswers],
  );

  const handleConfirmAnswer = useCallback(
    (
      confirmedAnswer: NonNullable<GetThreadQuery['thread']['nodes']>[number],
    ) => {
      setConfirmedAnswers((prevAnswers) => {
        const answers =
          prevAnswers?.filter(
            (answer) =>
              answer.parentQuestionId !== confirmedAnswer.parentQuestionId,
          ) ?? [];

        return [...answers, confirmedAnswer];
      });

      const nextQuestionToExpand = questions.find(
        (question) =>
          !isAnsweredQuestion(question) &&
          question.id !== confirmedAnswer.parentQuestionId,
      );

      if (nextQuestionToExpand) {
        setExpandedQuestionId(nextQuestionToExpand.id);
      } else {
        setExpandedQuestionId(null);
      }
    },
    [setConfirmedAnswers, setExpandedQuestionId, questions, expandedQuestionId],
  );

  async function onSubmit() {
    try {
      closeDialog?.('dialog-support-bulk-confirm-answer');
      await confirmAnswer({
        variables: {
          confirmAnswerInput: {
            questionIds: confirmedAnswers.map(
              (answer) => answer.parentQuestionId,
            ),
            workspaceId: workspace.id,
          },
        },
      });
      refetchQuestions();
    } catch (error) {
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Common:errors.genericMessage'),
        color: 'blackroomRed',
      });
      console.error(error);
    }
  }

  /* **************************** RENDERING HELPERS *************************** */

  const renderThread = useCallback(
    (question: QueriedQuestion) => {
      // API
      const { data: threadData, loading: loadingThread } = useGetThreadQuery({
        variables: {
          filter: { threadSourceId: question.id },
          sorter: { createdAt: SortDirection.Asc },
        },
      });

      // Memoize data
      const answers = useMemo(() => {
        return threadData?.thread.nodes ?? [];
      }, [threadData]);

      if (loadingThread) {
        return <ContentLoader />;
      }

      return (
        <Stack gap="xs">
          {answers.map((answer) => (
            <Card key={answer.id} shadow="md" padding="xs" withBorder>
              <Stack gap="xs">
                <Group justify="space-between" gap="sm">
                  <Group gap="xs" align="center">
                    <User size="sm" user={answer.createdBy} />
                    <Text fz="sm">
                      <Text span fw={FONT_WEIGHT.ultraLight}>
                        {upperFirst(
                          t('Support:QuestionMenu.answerCreatedAtPrefix'),
                        )}
                      </Text>{' '}
                      {formatDistance(new Date(answer.createdAt), Date.now(), {
                        addSuffix: true,
                      })}
                    </Text>
                  </Group>
                  {isConfirmedAnswer(answer, question) ? (
                    <IconConfirmed color={palette.blackroomGreen} />
                  ) : (
                    <ActionIcon
                      variant="transparent"
                      onClick={() => handleConfirmAnswer(answer)}
                    >
                      <IconNotConfirmed />
                    </ActionIcon>
                  )}
                </Group>
                {answer.description && (
                  <Spoiler
                    className={classes.answerDescription}
                    maxHeight={68}
                    showLabel={t('Support:Common.spoiler.showLabel')}
                    hideLabel={t('Support:Common.spoiler.hideLabel')}
                  >
                    {answer.description}
                  </Spoiler>
                )}
                {answer.resources && (
                  <Group gap={8}>
                    <Group gap={0}>
                      <IconAttachedFile size={ICON_SIZE_MAP.small} />{' '}
                      <Text c="blackroomBlack" fz="sm">
                        {t('Support:QuestionMenu.thread.resource')}:
                      </Text>
                    </Group>
                    <Box>
                      {answer.resources.map((resource) => (
                        <ResourceBadgeSimple
                          key={resource.id}
                          resource={resource}
                          size="sm"
                          onClick={() => {
                            if (resource.id) {
                              openResource(resource.id);
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Group>
                )}
              </Stack>
            </Card>
          ))}
        </Stack>
      );
    },
    [confirmedAnswers],
  );

  /* ******************************** RENDERING ******************************* */

  return (
    <Container
      w="50vw"
      p={0}
      onKeyDown={function cb(e) {
        switch (e.key) {
          case 'Enter':
            onSubmit();
            break;
          default:
            break;
        }
      }}
      fluid
    >
      <Stack align="stretch">
        {withIndicator && (
          <Group justify="flex-end">
            {unansweredQuestionCount > 0 ? (
              <Text>
                {t(
                  'Support:PageSupport.supportActions.dialogBulkConfirmAnswer.infoTexts.unansweredQuestions',
                  { unansweredQuestionCount },
                )}
              </Text>
            ) : (
              <Text c="blackroomGreen">
                {' '}
                {t(
                  'Support:PageSupport.supportActions.dialogBulkConfirmAnswer.infoTexts.answeredQuestions',
                )}
              </Text>
            )}
          </Group>
        )}
        <form onSubmit={(e) => e.preventDefault()}>
          <Accordion
            className={classes.accordion}
            variant="contained"
            value={expandedQuestionId}
            onChange={setExpandedQuestionId}
          >
            {questions.map((question) => (
              <Accordion.Item
                key={question.id}
                value={question.id}
                ref={(el) => (questionsRefs.current[question.id] = el)}
              >
                <Accordion.Control
                  classNames={{
                    control: classes.accordionControl,
                    label: classes.accordionLabel,
                  }}
                >
                  <Group pb="xs" align="center" justify="space-between">
                    <Group gap="xs" align="center">
                      <User size="sm" user={question.createdBy} />
                      <Text fz="sm">
                        <Text span fw={FONT_WEIGHT.ultraLight}>
                          {upperFirst(
                            t('Support:Common.questionCreatedAtPrefix'),
                          )}
                        </Text>{' '}
                        {formatDistance(
                          new Date(question.createdAt),
                          Date.now(),
                          {
                            addSuffix: true,
                          },
                        )}
                      </Text>
                      {isAnsweredQuestion(question) &&
                        question.answersCount > 0 && (
                          <IconAnswered color={palette.blackroomGreen} />
                        )}
                      {!question.answersCount && (
                        <Text size="sm">
                          (
                          {t(
                            'Support:PageSupport.supportActions.dialogBulkConfirmAnswer.accordion.items.emptyAnswers',
                          )}
                          )
                        </Text>
                      )}
                    </Group>
                    <QuestionPriorityBadge
                      questionPriority={question.priority}
                    />
                  </Group>
                  <Text className={classes.quesitonTitle}>
                    {question.id === expandedQuestionId
                      ? question.title
                      : truncate(question.title, { length: 240 })}
                  </Text>
                </Accordion.Control>
                <Accordion.Panel>{renderThread(question)}</Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </form>
        <ConfirmBar
          labelProps={{
            confirm: t('Common:cta.save'),
            cancel: t('Common:cta.cancel'),
          }}
          onConfirm={() => onSubmit()}
          onCancel={() => {
            dialogStore?.closeDialog?.('dialog-support-bulk-confirm-answer');
          }}
          disabled={!confirmedAnswers?.length}
        />
      </Stack>
    </Container>
  );
}

export default DialogBulkConfirmAnswer;
